import { useEffect, useState } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import { GET_DASHBOARD } from "./DashboardApi";
import useRetry from "./useRetry";

// The `filters_config` argument of the `getDashboard` query. All fields are
// optional/nullable because individual widgets only populate the subset they
// need (e.g. `scopesid` comes from an optional `selectedScope?.value?.id`).
interface SummaryFiltersConfig {
  filtersjson?: string | null;
  scopesjson?: string | null;
  // Wire-format key forwarded to `getDashboard`. Some widgets (e.g. the
  // RemediationQueue KPI card) pass a saved-filter id directly instead of an
  // inline `filtersjson`.
  filtersid?: string | null;
  scopesid?: string | null;
  scope_group?: string | null;
  filters_fields_to_override?: string[] | null;
  filter_data?: string | null;
}

// Variables for the `getDashboard` GraphQL query (see GET_DASHBOARD). Fields are
// optional because each widget passes only the arguments it requires.
export interface SummaryVariables {
  name: string;
  selection?: (string | null)[];
  group_by?: (string | null)[];
  aggregate_functions?: (string | null)[];
  round_functions?: (string | null)[];
  filters_config?: SummaryFiltersConfig;
  filter_negatives?: boolean | null;
  filter_null_groups?: boolean | null;
  max_results?: number | null;
}

// Raw shape of the `getDashboard` query response. The backend returns the
// `summary` field as a JSON-encoded string that is parsed below.
interface SummaryQueryData {
  summary: string;
}

export interface UseSummaryOptions {
  variables: SummaryVariables;
  skip?: boolean;
}

export interface UseSummaryReturn {
  // Parsed JSON payload of the summary response. The runtime shape is
  // widget-specific (each consumer narrows it to its own row-tuple type), so it
  // is exposed as `unknown` at this boundary.
  summaryData: unknown;
  summaryDataError: boolean;
  summaryLoading: boolean;
  refetchSummary: () => void;
}

const useSummary = ({
  variables,
  skip = false,
}: UseSummaryOptions): UseSummaryReturn => {
  const [summaryData, setSummaryData] = useState<unknown>(null);

  const { data, error, loading, refetch, networkStatus } = useQuery<
    SummaryQueryData,
    SummaryVariables
  >(GET_DASHBOARD, {
    variables: { ...variables },
    skip: skip,
    notifyOnNetworkStatusChange: true,
  });

  const { finalError, resetRetries } = useRetry({
    requestError: error,
    refetch,
  });

  useEffect(() => {
    if (!!data && !loading) {
      resetRetries();

      try {
        let parsedData = JSON.parse(data?.summary);
        setSummaryData(parsedData);
      } catch (e) {}
    }
  }, [data, loading]);

  return {
    summaryData,
    summaryDataError: !!finalError && networkStatus !== NetworkStatus.refetch,
    summaryLoading: loading || networkStatus === NetworkStatus.refetch,
    refetchSummary: refetch,
  };
};

export default useSummary;
